import { useCallback } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ReactionPicker } from './reaction-picker'
import { cn } from '@/lib/utils'
import { HexColorPicker } from 'react-colorful'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Palette, Smile, Pipette } from 'lucide-react'
const EmojiPickerTriggerContent = ({ emoji, color, size, defaultIcon }) => {
  if (emoji) {
    return <span className={cn(size === 'sm' ? 'text-sm' : 'text-xl')}>{emoji}</span>
  }
  if (color) {
    return (
      <div
        className={cn('rounded-full', size === 'sm' ? 'h-2 w-2' : 'h-3 w-3')}
        style={{
          backgroundColor: color,
        }}
      />
    )
  }
  if (defaultIcon) {
    return <>{defaultIcon}</>
  }
  return (
    <div
      className={cn('rounded-full', size === 'sm' ? 'h-2 w-2' : 'h-3 w-3')}
      style={{
        backgroundColor: '#3b82f6',
      }}
    />
  )
}
const ColorInputRow = ({ color, onColorInputChange, onEyeDropperClick }) => (
  <div className="flex items-center gap-2">
    <div
      className="w-8 h-8 rounded-full border shadow-sm shrink-0"
      style={{
        backgroundColor: color,
      }}
    />
    <input
      type="text"
      value={color}
      onChange={onColorInputChange}
      className="flex-1 bg-muted/50 border-none rounded-md px-3 py-1 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-primary/20"
    />
    <button
      onClick={onEyeDropperClick}
      className="p-1.5 hover:bg-muted rounded-md transition-colors text-muted-foreground hover:text-foreground shrink-0"
      title="Pick a color from screen"
    >
      <Pipette className="w-4 h-4" />
    </button>
  </div>
)

const ListEmojiPickerTabs = ({
  emoji,
  color,
  handleColorChange,
  handleColorInputChange,
  handleEmojiSelect,
  handleEyeDropperClick,
}) => (
  <Tabs defaultValue={emoji ? 'emoji' : 'color'} className="w-full">
    <TabsList className="w-full grid grid-cols-2 p-1 bg-transparent">
      <TabsTrigger
        value="color"
        className="rounded-sm data-[state=active]:bg-muted flex items-center justify-center gap-2"
      >
        <Palette className="w-4 h-4" />
        Color
      </TabsTrigger>
      <TabsTrigger
        value="emoji"
        className="rounded-sm data-[state=active]:bg-muted flex items-center justify-center gap-2"
      >
        <Smile className="w-4 h-4" />
        Emoji
      </TabsTrigger>
    </TabsList>

    <TabsContent value="color" className="mt-0 p-4">
      <div className="flex flex-col gap-4">
        <HexColorPicker
          color={color || '#3b82f6'}
          onChange={handleColorChange}
          style={{
            width: '100%',
            height: '200px',
            borderRadius: '8px',
          }}
        />
        <ColorInputRow
          color={color || '#3b82f6'}
          onColorInputChange={handleColorInputChange}
          onEyeDropperClick={handleEyeDropperClick}
        />
      </div>
    </TabsContent>

    <TabsContent value="emoji" className="mt-0">
      <ReactionPicker selectedEmojis={emoji ? [emoji] : []} onSelect={handleEmojiSelect} />
    </TabsContent>
  </Tabs>
)
export function ListEmojiPicker({ emoji, color, onSelect, size = 'md', defaultIcon }) {
  const stopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])
  const handleColorChange = useCallback(
    (nextColor) => {
      onSelect({
        color: nextColor,
        emoji: undefined,
      })
    },
    [onSelect],
  )
  const handleEmojiSelect = useCallback(
    (nextEmoji) => {
      onSelect({
        emoji: nextEmoji,
        color: undefined,
      })
    },
    [onSelect],
  )
  const handleColorInputChange = useCallback(
    (e) => {
      handleColorChange(e.target.value)
    },
    [handleColorChange],
  )
  const handleEyeDropperClick = useCallback(async () => {
    if (!window.EyeDropper) return
    const eyeDropper = new window.EyeDropper()
    try {
      const result = await eyeDropper.open()
      handleColorChange(result.sRGBHex)
    } catch (e) {
      console.log('EyeDropper failed', e)
    }
  }, [handleColorChange])
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'flex items-center justify-center shrink-0 transition-opacity hover:opacity-80 active:opacity-60',
            size === 'sm' ? 'h-6 w-6 rounded-sm' : 'h-8 w-8 rounded-md',
            !emoji && 'hover:bg-muted/50',
          )}
        >
          <EmojiPickerTriggerContent
            emoji={emoji}
            color={color}
            size={size}
            defaultIcon={defaultIcon}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(340px,calc(100vw-1rem))] p-0 rounded-md border-none shadow-2xl shadow-primary/5 bg-popover/95 backdrop-blur-xl overflow-hidden z-[100]"
        align="start"
        side="right"
        sideOffset={10}
      >
        <ListEmojiPickerTabs
          emoji={emoji}
          color={color}
          handleColorChange={handleColorChange}
          handleColorInputChange={handleColorInputChange}
          handleEmojiSelect={handleEmojiSelect}
          handleEyeDropperClick={handleEyeDropperClick}
        />
      </PopoverContent>
    </Popover>
  )
}
